import { useStore } from '../../../store/store';
import { UserEarnOfferListResponseDataItem, useUtxEarnOffersListOffers } from '../../../../../packages/utx-client/src/react';
import { useContext, useMemo, useState } from 'react';
import { useDebounce } from '@/utils/useDebounce.hook';
import { EarnContext } from '../contexts/useEarnContext';

export interface OffersByCategory {
  [key: string]: Array<UserEarnOfferListResponseDataItem>;
}
export interface ListOffer {
  asset: string;
  category: Array<string>;
  estimatedApy: string;
  id: string;
  minSubscriptionAmount: string;
  maxSubscriptionAmount: string;
  provider: string;
  subTitle: string;
  subscriptionAsset: string;
  tenor: [number, string] | null;
  title: string;
}

interface UseOffersParams {
  tags?: Array<string>;
}

export const useOffers = (params: UseOffersParams) => {
  const { tags } = params;
  const store = useStore();
  const earnContext = useContext(EarnContext);

  // after 1000ms enabled
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken && store.users?.metadata.earnAccount?.enabled);
  }, 100);

  const [searchText, setSearchText] = useState<string | undefined>('');
  const debouncedSearchText = useDebounce(searchText);
  const [showMyAssetsOnly, setShowMyAssetsOnly] = useState<boolean>(false);
  const offers = useUtxEarnOffersListOffers(
    {
      asset: debouncedSearchText ? debouncedSearchText.toUpperCase() : undefined,
      assetsWithBalanceOnly: showMyAssetsOnly,
      tags
    },
    {
      query: {
        queryKey: ['offers', debouncedSearchText, showMyAssetsOnly, tags, store.authToken, store.users?.metadata.earnAccount?.enabled],
        retry: false,
        enabled,
        keepPreviousData: true,
        onSuccess: (data) => {
          if (!params.tags) {
            const categories = data.data.data.map((offer) => offer.category).sort((a, b) => a.localeCompare(b));
            const uniqueCategories = [...new Set(categories)];
            earnContext.setOfferCategories(uniqueCategories);
          }
        }
      }
    }
  );

  function handleSearch(search?: string) {
    setSearchText(search);
  }

  function toggleShowMyAssetsOnly() {
    setShowMyAssetsOnly(!showMyAssetsOnly);
  }

  const offersByCategory = offers.data?.data?.data.reduce((acc, offer) => {
    if (!offer.available) return acc;
    const category = offer.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(offer);
    return acc;
  }, {} as Record<string, Array<UserEarnOfferListResponseDataItem>>);

  return {
    offers,
    offersByCategory,
    handleSearch,
    showMyAssetsOnly,
    toggleShowMyAssetsOnly
  };
};
